import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { FolderOpen, User, LogOut, Menu, X, Zap, Moon, Sun } from 'lucide-react';

export default function ClientLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [dark, setDark] = useState(() => localStorage.getItem('theme') === 'dark');

  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
    localStorage.setItem('theme', dark ? 'dark' : 'light');
  }, [dark]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const initials = user?.name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || 'CL';

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg)', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
      {/* Top nav */}
      <nav
        className="h-14 flex items-center justify-between px-6 border-b sticky top-0 z-40"
        style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}
        data-testid="client-nav"
      >
        {/* Brand / Logo */}
        <div className="flex items-center gap-2.5">
          <div
            className="w-7 h-7 rounded-lg flex items-center justify-center"
            style={{ background: 'var(--brand-primary, #006080)' }}
          >
            <Zap size={14} color="white" strokeWidth={2.5} />
          </div>
          <span className="font-bold text-base" style={{ color: 'var(--ink)' }}>
            {user?.brand_name || 'Krris OS'}
          </span>
        </div>

        {/* Desktop nav links */}
        <div className="hidden md:flex items-center gap-1">
          <NavLink
            to="/portal"
            end
            data-testid="client-nav-projects"
            className={({ isActive }) =>
              `flex items-center gap-1.5 px-3 py-1.5 rounded-btn text-sm font-medium transition-colors ${isActive
                ? 'text-primary bg-primary/10'
                : 'hover:bg-black/5'
              }`
            }
            style={({ isActive }) => ({ color: isActive ? '#006080' : 'var(--muted)' })}
          >
            <FolderOpen size={15} strokeWidth={2} />
            My Projects
          </NavLink>
          <NavLink
            to="/portal/profile"
            data-testid="client-nav-profile"
            className={({ isActive }) =>
              `flex items-center gap-1.5 px-3 py-1.5 rounded-btn text-sm font-medium transition-colors ${isActive
                ? 'text-primary bg-primary/10'
                : 'hover:bg-black/5'
              }`
            }
            style={({ isActive }) => ({ color: isActive ? '#006080' : 'var(--muted)' })}
          >
            <User size={15} strokeWidth={2} />
            Profile
          </NavLink>
        </div>

        {/* Right actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setDark(!dark)}
            className="w-8 h-8 flex items-center justify-center rounded-btn transition-colors hover:bg-black/5"
            style={{ color: 'var(--muted)' }}
            data-testid="client-theme-toggle"
          >
            {dark ? <Sun size={16} /> : <Moon size={16} />}
          </button>

          {/* User avatar */}
          <div className="flex items-center gap-2">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white cursor-pointer"
              style={{ background: '#006080' }}
              data-testid="client-user-avatar"
            >
              {initials}
            </div>
          </div>

          <button
            onClick={handleLogout}
            className="hidden md:flex items-center gap-1 text-sm px-2 py-1.5 rounded-btn transition-colors hover:bg-black/5"
            style={{ color: 'var(--muted)' }}
            data-testid="client-logout-button"
          >
            <LogOut size={14} />
          </button>

          {/* Mobile menu */}
          <button
            className="md:hidden"
            onClick={() => setMenuOpen(!menuOpen)}
            style={{ color: 'var(--muted)' }}
          >
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      {menuOpen && (
        <div
          className="md:hidden border-b px-4 py-3 space-y-1"
          style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}
        >
          <NavLink to="/portal" end className="flex items-center gap-2 px-3 py-2 rounded-btn text-sm" style={{ color: 'var(--ink)' }} onClick={() => setMenuOpen(false)}>
            <FolderOpen size={15} /> My Projects
          </NavLink>
          <NavLink to="/portal/profile" className="flex items-center gap-2 px-3 py-2 rounded-btn text-sm" style={{ color: 'var(--ink)' }} onClick={() => setMenuOpen(false)}>
            <User size={15} /> Profile
          </NavLink>
          <button onClick={handleLogout} className="flex items-center gap-2 px-3 py-2 rounded-btn text-sm w-full" style={{ color: 'var(--danger)' }}>
            <LogOut size={15} /> Sign out
          </button>
        </div>
      )}

      {/* Page content */}
      <main data-testid="client-main-content">
        <Outlet />
      </main>
    </div>
  );
}
